import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {AreaChart,Area,BarChart,Bar,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,Cell,} from "recharts";
import {User,Shield,CreditCard,Wallet,TrendingUp,AlertTriangle,CheckCircle,Clock,Award,Calendar,DollarSign,Activity,ChevronRight,Star,FileText,Banknote,
  BadgeCheck,ArrowUpRight,Target,Layers,} from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmtINR = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (iso, opts = { day: "numeric", month: "short", year: "numeric" }) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", opts) : "—";
const mkInitials = (name) =>
  name ? name.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() : "US";

/* ─────────────────────────────────────────────
   STATUS COLOURS
───────────────────────────────────────────── */
const statusColors = {
  approved: "text-emerald-600 bg-emerald-50 border-emerald-200",
  pending:  "text-amber-600  bg-amber-50  border-amber-200",
  rejected: "text-red-600    bg-red-50    border-red-200",
  paid:     "text-emerald-600 bg-emerald-50 border-emerald-200",
  overdue:  "text-red-600    bg-red-50    border-red-200",
  prospect: "text-violet-600 bg-violet-50 border-violet-200",
};

/* ─────────────────────────────────────────────
   MINI COMPONENTS
───────────────────────────────────────────── */
function Badge({ status, label }) {
  const color = statusColors[status] ?? "text-gray-600 bg-gray-50 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${color} capitalize`}>
      {label ?? status}
    </span>
  );
}

function StatCard({ icon, label, value, sub, accent, trend }) {
  return (
    <div className="bg-card border border-card-border rounded-xl p-5 flex flex-col gap-3 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent ?? "bg-primary/10 text-primary"}`}>
          {icon}
        </div>
        {trend && (
          <span className="text-xs text-emerald-600 font-medium flex items-center gap-0.5">
            <ArrowUpRight size={12} />
            {trend}
          </span>
        )}
      </div>
      <div>
        <div className="text-2xl font-bold text-foreground tracking-tight">{value}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
      </div>
      {sub && <div className="text-xs text-muted-foreground border-t border-border pt-2">{sub}</div>}
    </div>
  );
}

function SectionHeading({ icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-primary">{icon}</span>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-white/5 last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`text-sm font-medium text-gray-900 dark:text-white ${mono ? "font-mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

/* ═════════════════════════════════════════════
   MAIN DASHBOARD
═════════════════════════════════════════════ */
const UserDashboard = () => {
  const [data,        setData]        = useState(null);
  const [isLoading,   setIsLoading]   = useState(true);
  const [error,       setError]       = useState(null);
  const [profileImg,  setProfileImg]  = useState(null);
  const [imageError,  setImageError]  = useState(false);
  const [activeTab,   setActiveTab]   = useState("all"); // "all" | "paid" | "pending"

  /* ── 1. Fetch all dashboard data ── */
  useEffect(() => {
    const load = async () => {
      try {
        const results = await Promise.allSettled([
          axiosInstance.get("/profile/get"),        // 0
          axiosInstance.get("/kyc/get"),             // 1
          axiosInstance.get("/bank/get"),            // 2
          axiosInstance.get("/pay/all"),             // 3
          axiosInstance.get("/installments/all"),    // 4
          axiosInstance.get("/installments/pending"),// 5
          axiosInstance.get("/plan/get"),            // 6
          axiosInstance.get("/commission/get"),      // 7
        ]);

        const ok  = (i) => results[i].status === "fulfilled";
        const val = (i, fb) => ok(i) ? results[i].value.data : fb;

        const profile     = val(0, {});
        const kyc         = val(1, {});
        const bank        = val(2, {});
        const payments    = val(3, []);
        const allInst     = val(4, []);
        const instPending = val(5, { summary: {}, pending: [] });
        const planRaw     = val(6, []);
        const commission  = val(7, {});

        const planData  = Array.isArray(planRaw) ? (planRaw[0] ?? {}) : planRaw;

        const totalMonths = Number(planData.count   || 24);
        const paidCount   = Number(instPending.summary?.paid_installments || 0);
        const emiAmount   = Number(planData.joining  || 0);
        const totalValue  = Number(planData.total    || 0);
        const paidAmount  = paidCount * emiAmount;
        const remaining   = totalValue - paidAmount;
        const progress    = totalMonths > 0 ? Math.round((paidCount / totalMonths) * 100) : 0;

        const pendingArr  = Array.isArray(instPending.pending) ? instPending.pending : [];
        const nextDue     = pendingArr[0] ?? null;

        /* installment bar-chart data */
        const installmentChartData = Array.from({ length: totalMonths }, (_, i) => {
          const monthNo = i + 1;
          let status = "upcoming";
          if (monthNo <= paidCount) status = "paid";
          else if (nextDue && monthNo === nextDue.no) status = nextDue.overdue > 0 ? "overdue" : "due";
          return { month: `M${monthNo}`, amount: emiAmount, status };
        });

        /* payment timeline (area chart) */
        const paymentTimelineData = (Array.isArray(payments) ? payments : [])
          .slice()
          .sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at))
          .map((p) => ({
            label:  fmtDate(p.submitted_at, { day: "numeric", month: "short" }),
            amount: Number(p.amount),
            type:   p.type,
          }));
        if (nextDue) {
          paymentTimelineData.push({
            label:  `${fmtDate(nextDue.due, { day: "numeric", month: "short" })} (due)`,
            amount: nextDue.total,
            type:   "Overdue",
          });
        }

        setData({
          profile: {
            id:         profile.id         || "—",
            name:       profile.name       || "Unknown User",
            email:      profile.email      || "—",
            phone:      profile.phone      || "—",
            address:    profile.address    || "—",
            city:       profile.city       || "—",
            state:      profile.state      || "—",
            pincode:    profile.pincode    || "—",
            membership: profile.membership || "Prospect",
            created_at: profile.created_at || null,
            initials:   mkInitials(profile.name),
          },
          kyc: {
            aadhar:       kyc.aadhar       || "—",
            pan:          kyc.pan          || "—",
            status:       kyc.status       || "pending",
            submitted_on: kyc.submitted_on || null,
          },
          bank: {
            holder:  bank.holder  || "—",
            bank:    bank.bank    || "—",
            account: bank.account || "—",
            ifsc:    bank.ifsc    || "—",
            branch:  bank.branch  || "—",
            status:  bank.status  || "pending",
          },
          plan: {
            id:      planData.id      || "—",
            name:    planData.name    || "Standard Plan",
            joining: emiAmount,
            total:   totalValue,
            count:   totalMonths,
          },
          commission: {
            earned:    Number(commission.earned    || 0),
            redeemed:  Number(commission.redeemed  || 0),
            balance:   Number(commission.balance   || 0),
            forfeited: Number(commission.forfeited || 0),
          },
          emi: { paidCount, paidAmount, remaining, progress, totalMonths, emiAmount },
          summary: {
            total_installments: totalMonths,
            paid_installments:  paidCount,
            pending_count:      instPending.summary?.pending_count || 0,
            total_penalty:      Number(instPending.summary?.total_penalty || 0),
          },
          nextDue,
          payments:    Array.isArray(payments) ? payments : [],
          installments:Array.isArray(allInst)  ? allInst  : [],
          pendingArr,
          installmentChartData,
          paymentTimelineData,
        });
      } catch (err) {
        console.error("Dashboard Error:", err);
        setError("Failed to load dashboard data.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  /* ── 2. Fetch profile image ── */
  useEffect(() => {
    if (!data?.profile?.id || data.profile.id === "—") return;
    let url = null;
    const fetchImg = async () => {
      try {
        const res = await axiosInstance.get(`/profile/get-image/${data.profile.id}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setProfileImg(url);
      } catch {
        setImageError(true);
      }
    };
    fetchImg();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [data?.profile?.id]);

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-9 h-9 text-primary animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <p className="text-sm text-muted-foreground">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (error || !data) return null;

  const { profile, kyc, bank, plan, commission, emi, summary, nextDue,
          payments, installments, pendingArr, installmentChartData, paymentTimelineData } = data;

  /* payment table filter */
  const filteredPayments =
    activeTab === "all"     ? payments :
    activeTab === "paid"    ? payments.filter((p) => p.status === "approved") :
                              payments.filter((p) => p.status !== "approved");

  /* SVG donut geometry */
  const R         = 64;
  const circ      = 2 * Math.PI * R;
  const paidArc   = circ * emi.progress / 100;
  const remainArc = circ * (1 - emi.progress / 100);

  return (
    <div className="min-h-screen bg-background">
      {/* Top accent bar */}
      

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

        {/* ══ WELCOME BANNER ══ */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-sky-600 to-indigo-700 text-white p-6 mb-8 shadow-xl">
          {/* decorative rings */}
          <div className="absolute right-0 top-0 h-full w-64 opacity-10 pointer-events-none">
            <div className="absolute top-4 right-8  w-32 h-32 rounded-full border-4 border-white" />
            <div className="absolute top-12 right-20 w-48 h-48 rounded-full border-2 border-white" />
          </div>

          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          {/* avatar */}
          <div className="w-24 h-24 rounded-full bg-white/20 border border-white/30 overflow-hidden flex items-center justify-center text-4xl font-bold shrink-0">
            {profileImg && !imageError
              ? <img src={profileImg} alt="Profile" className="w-full h-full object-cover" onError={() => setImageError(true)} />
              : profile.initials}
          </div>

            <div className="flex-1">
              <div className="text-sm text-white/70 mb-0.5">Welcome back</div>
              <h1 className="text-2xl font-bold">{profile.name}</h1>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 border border-white/30 rounded-full px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  KYC {kyc.status}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 border border-white/30 rounded-full px-3 py-1 capitalize">
                  <Star size={10} />
                  {profile.membership}
                </span>
                {profile.created_at && (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 border border-white/30 rounded-full px-3 py-1">
                    <Calendar size={10} />
                    Joined {fmtDate(profile.created_at)}
                  </span>
                )}
              </div>
            </div>

            <div className="text-right hidden md:block">
              <div className="text-white/60 text-xs mb-1">Member ID</div>
              <div className="font-mono text-sm font-semibold bg-white/10 rounded-lg px-3 py-1.5">{profile.id}</div>
            </div>
          </div>
        </div>

        {/* ══ OVERDUE ALERT ══ */}
        {nextDue && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-800 text-sm">
                {nextDue.overdue > 0 ? "Overdue Installment — Action Required" : `Installment #${nextDue.no} Due Soon`}
              </div>
              <div className="text-amber-700 text-xs mt-0.5">
                Installment #{nextDue.no} of ₹{fmtINR(nextDue.total)} is
                {nextDue.overdue > 0 ? ` overdue by ${nextDue.overdue} day(s). Includes ₹${fmtINR(nextDue.penalty)} penalty.` : " due soon."}
                {" "}Deadline: {fmtDate(nextDue.deadline)}
              </div>
            </div>
            <Link
              to="/user/payments"
              className="shrink-0 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded-lg px-3 py-1.5 hover:bg-amber-200 transition-colors"
            >
              Pay Now
            </Link>
          </div>
        )}

        {/* ══ ASYMMETRIC TWO-COLUMN LAYOUT ══ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">

          {/* ════════════════════════════════════
              LEFT COLUMN (wider)
          ════════════════════════════════════ */}
          <div className="flex flex-col gap-6">

            {/* ── Stat Cards Row ── */}
            {/* ── Stat Cards Row ── */}
        <div className="bg-white dark:bg-[#1e293b] border-gray-200 dark:border-white/5 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard
            icon={<DollarSign size={18} />}
            label="Commission Earned"
            value={`₹${fmtINR(commission.earned)}`}
            accent="bg-violet-100 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400"
            trend="Active"
          />
          <StatCard
            icon={<Wallet size={18} />}
            label="Wallet Balance"
            value={`₹${fmtINR(commission.balance)}`}
            accent="bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            sub="Available for redemption"
          />
          <StatCard
            icon={<Layers size={18} />}
            label="Installments Paid"
            value={`${summary.paid_installments}/${summary.total_installments}`}
            accent="bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400"
            sub={`${summary.total_installments - summary.paid_installments} remaining`}
          />
          <StatCard
            icon={<Target size={18} />}
            label="Total Plan Value"
            value={`₹${fmtINR(plan.total)}`}
            accent="bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
            sub={plan.name}
          />
        </div>

            {/* ── Installment Progress + Bar Chart ── 1e293b*/}
            {/* To this: */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/30 rounded-xl p-5 shadow-lg ">
            
              <SectionHeading icon={<Activity size={16} />} title="Installment Progress" />

              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                    <span>{emi.paidCount} paid</span>
                    <span>{emi.progress}% complete</span>
                  </div>
                  {/* To this: */}
              <div className="h-2.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden shadow-inner">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all duration-700"
                      style={{ width: `${emi.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                    <span>₹{fmtINR(emi.paidAmount)} paid</span>
                    <span>₹{fmtINR(emi.remaining)} remaining</span>
                  </div>
                </div>
                <div className="text-center hidden sm:block">
                  <div className="text-3xl font-bold text-primary">{emi.progress}%</div>
                  <div className="text-xs text-muted-foreground">Complete</div>
                </div>
              </div>

           {/* Bar chart */}
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={installmentChartData}
                  barSize={8}
                  margin={{ top: 0, right: 0, left: -24, bottom: 0 }}
                >
                  {/* Reduced stroke opacity so the grid doesn't glare in dark mode */}
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#9ca3af" strokeOpacity={0.2} />
                  
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 9, fill: "#9ca3af" }} 
                    interval={Math.floor(emi.totalMonths / 6)}
                  />
                  {/* Universal grey-400 applied to YAxis */}
                  <YAxis tick={{ fontSize: 9, fill: "#9ca3af" }} /> 
                  
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937", // Dark grey background
                      borderColor: "#374151",     // Slightly lighter grey border
                      borderRadius: 8,
                      fontSize: 12,
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3)" // Soft drop shadow
                    }}
                    itemStyle={{ color: "#f9fafb", fontWeight: "bold" }} // White text for the value
                    labelStyle={{ color: "#9ca3af", marginBottom: "4px" }} // Grey text for the title (e.g., M1)
                    formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, "Amount"]}
                  />
                  
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {installmentChartData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={
                          entry.status === "paid"    ? "#10b981" :
                          entry.status === "overdue" ? "#f59e0b" :
                          entry.status === "due"     ? "#f59e0b" :
                          "#4b5563" // Changed to gray-600 so upcoming bars are visible but muted in both themes
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
              <div className="flex gap-4 mt-3 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Paid</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> Overdue / Due</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-muted inline-block border border-border" /> Upcoming</span>
              </div>
            </div>

            {/* ── Payment Timeline Area Chart ── */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/30 rounded-xl p-5 shadow-lg ">
              <SectionHeading icon={<TrendingUp size={16} />} title="Payment Timeline" />
              {paymentTimelineData.length > 0 ? (
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={paymentTimelineData}
                      margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="payGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#6d5cf7" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#6d5cf7" stopOpacity={0}    />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} />
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, "Amount"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="#6d5cf7"
                        strokeWidth={2.5}
                        fill="url(#payGradient)"
                        dot={{ fill: "#6d5cf7", strokeWidth: 2, r: 5 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-44 flex items-center justify-center text-sm text-muted-foreground">
                  No payment data yet.
                </div>
              )}
            </div>

            {/* ── Payment History Table ── */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/20 rounded-xl p-5 shadow-lg">
              <div className="p-5 pb-0">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-primary" />
                    <h2 className="text-base font-semibold">Payment History</h2>
                  </div>
                  <div className="flex gap-1.5">
                    {["all", "paid", "pending"].map((t) => (
                      <button
                        key={t}
                        onClick={() => setActiveTab(t)}
                        className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors capitalize ${
                          activeTab === t
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      {["Transaction", "Type", "Method", "Amount", "Status", "Date"].map((h) => (
                        <th key={h} className={`text-left py-2.5 font-medium text-muted-foreground text-xs ${h === "Transaction" || h === "Date" ? "px-5" : "px-3"}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.length > 0 ? filteredPayments.map((p) => (
                      <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="py-3 px-5">
                          <div className="font-mono text-xs text-muted-foreground">{p.transaction || p.id?.slice(-10)}</div>
                          <div className="text-xs mt-0.5">{p.sender || profile.name}</div>
                        </td>
                        <td className="py-3 px-3 capitalize text-xs font-medium">{p.type}</td>
                        <td className="py-3 px-3">
                          <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md font-medium">
                            {p.method || "UPI"}
                          </span>
                        </td>
                        <td className="py-3 px-3 font-semibold">₹{fmtINR(p.amount)}</td>
                        <td className="py-3 px-3">
                          <Badge status={p.status} label={p.status === "approved" ? "Verified" : p.status} />
                        </td>
                        <td className="py-3 px-5 text-xs text-muted-foreground">{fmtDate(p.submitted_at)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No payments found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Installment Records Table ── */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/20 rounded-xl shadow-lg overflow-hidden">
            <div className="p-5 pb-0">
              <SectionHeading icon={<Calendar size={16} />} title="Installment Records" />
            </div>
            <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
              <table className="w-full text-sm">
                <thead>
                  {/* Table Header Row */}
                  <tr className="border-b border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5">
                    {["#", "Month", "Amount", "Status", "Paid On"].map((h) => (
                      <th key={h} className={`text-left py-2.5 font-medium text-gray-500 dark:text-gray-400 text-xs ${h === "#" || h === "Paid On" ? "px-5" : "px-3"}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Paid Installment Rows */}
                  {installments.map((ins) => (
                    <tr key={ins.no} className="border-b border-gray-200 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                      <td className="py-3 px-5">
                        <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs font-bold flex items-center justify-center">
                          {ins.no}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-900 dark:text-gray-200">{ins.month}</td>
                      <td className="py-3 px-3 font-semibold text-gray-900 dark:text-white">₹{fmtINR(ins.amount)}</td>
                      <td className="py-3 px-3"><Badge status={ins.status} /></td>
                      <td className="py-3 px-5 text-xs text-gray-500 dark:text-gray-400">{fmtDate(ins.paid_at)}</td>
                    </tr>
                  ))}
                  
                  {/* Pending Installment Rows (Highlighted) */}
                  {pendingArr.map((p) => (
                    <tr key={`pending-${p.no}`} className="border-b border-gray-200 dark:border-white/5 last:border-0 bg-amber-50/50 dark:bg-amber-500/10 hover:bg-amber-50 dark:hover:bg-amber-500/20 transition-colors">
                      <td className="py-3 px-5">
                        <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 text-xs font-bold flex items-center justify-center">
                          {p.no}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm text-amber-700 dark:text-amber-400 font-medium">
                        Due {fmtDate(p.due, { day: "numeric", month: "short" })}
                      </td>
                      <td className="py-3 px-3 font-semibold text-amber-700 dark:text-amber-400">₹{fmtINR(p.total)}</td>
                      <td className="py-3 px-3">
                        <Badge status={p.overdue > 0 ? "overdue" : "pending"} label={p.overdue > 0 ? "Overdue" : "Pending"} />
                      </td>
                      <td className="py-3 px-5 text-xs text-amber-600 dark:text-amber-400/80">+₹{fmtINR(p.penalty)} penalty</td>
                    </tr>
                  ))}
                  
                  {/* Empty State */}
                  {installments.length === 0 && pendingArr.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No installment records yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          </div>{/* end left column */}

          {/* ════════════════════════════════════
              RIGHT COLUMN (380px)
          ════════════════════════════════════ */}
          <div className="flex flex-col gap-6">

           {/* ── Commission Summary + Donut ── */}
        <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
          <SectionHeading icon={<Award size={16} />} title="Commission Summary" />

          {/* SVG Donut Ring */}
          <div className="flex items-center justify-center py-4">
            <div className="relative">
              <svg width="160" height="160" viewBox="0 0 160 160">
                {/* Background Track */}
                <circle cx="80" cy="80" r={R} fill="none" className="stroke-gray-200 dark:stroke-slate-700" strokeWidth="16" />
                {/* Progress Track */}
                <circle
                  cx="80" cy="80" r={R}
                  fill="none"
                  stroke="#6d5cf7"
                  strokeWidth="16"
                  strokeDasharray={`${paidArc} ${remainArc}`}
                  strokeLinecap="round"
                  transform="rotate(-90 80 80)"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-3xl font-bold text-violet-600 dark:text-violet-400">{emi.progress}%</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Plan Paid</div>
                <div className="text-xs font-medium text-gray-900 dark:text-white mt-0.5">
                  {summary.paid_installments}/{summary.total_installments}
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-6 text-xs mb-3 flex-wrap text-gray-600 dark:text-gray-400 font-medium">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block" />
              Paid (₹{fmtINR(emi.paidAmount)})
            </span>
            <span className="flex items-center gap-1.5">
              {/* Updated the "Remaining" grey dot for dark mode */}
              <span className="w-2.5 h-2.5 rounded-full bg-gray-200 dark:bg-slate-700 inline-block border border-gray-300 dark:border-slate-600" />
              Remaining (₹{fmtINR(emi.remaining)})
            </span>
          </div>

          {/* Summary Boxes */}
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div className="rounded-xl bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-500/20 p-3 text-center transition-colors">
              <div className="text-xl font-bold text-violet-700 dark:text-violet-400">₹{fmtINR(commission.earned)}</div>
              <div className="text-xs text-violet-500 dark:text-violet-400/80 mt-0.5">Total Earned</div>
            </div>
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 p-3 text-center transition-colors">
              <div className="text-xl font-bold text-emerald-700 dark:text-emerald-400">₹{fmtINR(commission.balance)}</div>
              <div className="text-xs text-emerald-500 dark:text-emerald-400/80 mt-0.5">Available</div>
            </div>
          </div>

          <Link
            to="/user/wallet"
            className="mt-4 w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            <Wallet size={14} />
            Redeem Commission
          </Link>
        </div>
        
            {/* ── Plan Details ── */}
          <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
            <SectionHeading icon={<Layers size={16} />} title="Plan Details" />
            
            {/* Updated Gradient Wrapper for Dark Mode */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-500/10 dark:to-indigo-500/10 border border-violet-100 dark:border-violet-500/20 mb-4 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                <Star size={16} className="text-white" />
              </div>
              <div>
                {/* Updated Text Colors */}
                <div className="font-semibold text-sm text-gray-900 dark:text-white">{plan.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">ID: {plan.id}</div>
              </div>
            </div>
            
            <InfoRow label="Joining Fee"        value={`₹${fmtINR(plan.joining)}`} />
            <InfoRow label="Total Plan Value"   value={`₹${fmtINR(plan.total)}`}   />
            <InfoRow label="Total Installments" value={`${plan.count} months`}      />
            <InfoRow label="Per Month"          value={`₹${fmtINR(plan.joining)}`} />
          </div>

            {/* ── Profile Details ── */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/20 rounded-xl p-5 shadow-lg">
              <SectionHeading icon={<User size={16} />} title="Profile Details" />
              <InfoRow label="Name"    value={profile.name}  />
              <InfoRow label="Email"   value={profile.email} />
              <InfoRow label="Phone"   value={profile.phone} mono />
              <InfoRow label="City"    value={`${profile.city}, ${profile.state}`} />
              <InfoRow label="Pincode" value={profile.pincode} mono />
              <div className="mt-3 flex gap-2 flex-wrap">
                <Badge status={profile.membership.toLowerCase()} label={profile.membership} />
                <Badge status={kyc.status} label={`KYC: ${kyc.status}`} />
              </div>
            </div>

            {/* ── KYC Verification ── */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
              <SectionHeading icon={<Shield size={16} />} title="KYC Verification" />
              
              {/* Status Banner */}
              <div className={`flex items-center gap-3 mb-4 p-3 rounded-xl border transition-colors ${
                kyc.status === "approved"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20"
                  : "bg-amber-50 dark:bg-amber-500/10 border-amber-100 dark:border-amber-500/20"
              }`}>
                <BadgeCheck size={20} className={kyc.status === "approved" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-500 dark:text-amber-400"} />
                <div>
                  <div className={`font-semibold text-sm ${kyc.status === "approved" ? "text-emerald-800 dark:text-emerald-400" : "text-amber-800 dark:text-amber-400"}`}>
                    {kyc.status === "approved" ? "Verified & Approved" : `Status: ${kyc.status}`}
                  </div>
                  {kyc.submitted_on && (
                    <div className={`text-xs ${kyc.status === "approved" ? "text-emerald-600 dark:text-emerald-400/80" : "text-amber-600 dark:text-amber-400/80"}`}>
                      {fmtDate(kyc.submitted_on, { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                  )}
                </div>
              </div>

              {kyc.aadhar !== "—" && (
                <InfoRow label="Aadhaar" value={`XXXX XXXX ${kyc.aadhar.slice(-4)}`} mono />
              )}
              {kyc.pan !== "—" && (
                <InfoRow label="PAN" value={kyc.pan} mono />
              )}
              
              {/* Document Checklist */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                {["Aadhaar Front", "Aadhaar Back", "PAN Front", "PAN Back"].map((doc) => (
                  <div key={doc} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-white/5 rounded-lg px-2.5 py-2 border border-gray-200 dark:border-white/5 transition-colors">
                    <CheckCircle size={12} className={`shrink-0 ${kyc.status === "approved" ? "text-emerald-500 dark:text-emerald-400" : "text-gray-300 dark:text-gray-600"}`} />
                    {doc}
                  </div>
                ))}
              </div>
            </div>
            {/* ── Bank Account ── */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
              <SectionHeading icon={<Banknote size={16} />} title="Bank Account" />
              
              {/* Status Banner */}
              <div className={`flex items-center gap-3 mb-4 p-3 rounded-xl border transition-colors ${
                bank.status === "approved"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20"
                  : "bg-amber-50 dark:bg-amber-500/10 border-amber-100 dark:border-amber-500/20"
              }`}>
                <CheckCircle size={20} className={bank.status === "approved" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-500 dark:text-amber-400"} />
                <div>
                  <div className={`font-semibold text-sm ${bank.status === "approved" ? "text-emerald-800 dark:text-emerald-400" : "text-amber-800 dark:text-amber-400"}`}>
                    {bank.status === "approved" ? "Account Verified" : `Status: ${bank.status}`}
                  </div>
                  <div className={`text-xs ${bank.status === "approved" ? "text-emerald-600 dark:text-emerald-400/80" : "text-amber-600 dark:text-amber-400/80"}`}>
                    {bank.bank}
                  </div>
                </div>
              </div>

              <InfoRow label="Account Holder" value={bank.holder} />
              {bank.account !== "—" && (
                <InfoRow label="Account No." value={`XXXX XXXX ${bank.account.slice(-4)}`} mono />
              )}
              <InfoRow label="IFSC Code" value={bank.ifsc}   mono />
              <InfoRow label="Branch"    value={bank.branch} />
            </div>

            {/* ── Quick Actions ── */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
              <SectionHeading icon={<Activity size={16} />} title="Quick Actions" />
              <div className="flex flex-col gap-2.5">
                {[
                  { 
                    label: "Pay Overdue Installment", 
                    icon: <CreditCard size={14} />, 
                    color: "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/20",    
                    to: "/dashboard/payments" 
                  },
                  { 
                    label: "Redeem Commission",        
                    icon: <Wallet size={14} />, 
                    color: "text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-500/20 hover:bg-violet-100 dark:hover:bg-violet-500/20", 
                    to: "/dashboard/wallet"   
                  },
                  { 
                    label: "View KYC Documents",       
                    icon: <Shield size={14} />, 
                    color: "text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/20",         
                    to: "/dashboard/kyc"      
                  },
                  { 
                    label: "Download Statement",        
                    icon: <FileText size={14} />, 
                    color: "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10",         
                    to: "/dashboard/payments" 
                  },
                ].map((action) => (
                  <Link
                    key={action.label}
                    to={action.to}
                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${action.color}`}
                  >
                    <span className="flex items-center gap-2.5">
                      {action.icon}
                      {action.label}
                    </span>
                    <ChevronRight size={14} className="opacity-50 dark:opacity-70" />
                  </Link>
                ))}
              </div>
            </div>

          </div>{/* end right column */}
        </div>

        {/* ── Footer ── */}
        <div className="mt-8 text-center text-xs text-muted-foreground pb-4">
          Member ID: {profile.id}&nbsp;|&nbsp;Last updated: {new Date().toLocaleDateString("en-IN")}
        </div>

      </div>
    </div>
  );
};

export default UserDashboard;